/**
 * Shared API client factory for browser and server usage
 * @package @app/api
 */

import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { RouterClient } from '@orpc/server';
import type { Router } from './routers';

type Client = RouterClient<Router>;

export type { Client as ApiClient };

/**
 * Resolve the oRPC base URL.
 *
 * Default (no VITE_API_URL set): use the current origin's `/rpc`. The
 * handler lives inside apps/www itself — see
 * `apps/www/app/routes/rpc/$.ts`. This is the production path.
 *
 * Override (VITE_API_URL set): hit the standalone apps/api Hono server.
 * Useful for local dev when running both apps side-by-side, or if
 * apps/api is ever deployed to its own subdomain.
 *
 * On the server during SSR we can't rely on a relative URL — Node's
 * fetch needs an absolute one. Fall back to localhost:3000 (the www
 * app's dev port) so `ensureQueryData` calls inside route loaders still
 * work locally.
 */
function resolveApiUrl(): string {
  try {
    // biome-ignore lint/suspicious/noExplicitAny: import.meta.env requires Vite types not available in this package
    const env = (import.meta as any).env as Record<string, string> | undefined;
    if (env?.VITE_API_URL) return env.VITE_API_URL;
  } catch {
    // import.meta.env not available in non-Vite contexts
  }
  if (process.env.VITE_API_URL) return process.env.VITE_API_URL;

  // Browser: same-origin (colocated /rpc route in apps/www).
  if (typeof window !== 'undefined') return window.location.origin;

  // Node SSR: derive from Vercel env vars when available, else localhost.
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return 'http://localhost:3000';
}

/**
 * Optional explicit base URL override. SSR callers should pass the
 * incoming request's serving host so intra-app fetches stay on the same
 * alias the user is browsing (matters for Vercel Deployment Protection —
 * a per-deployment hash URL has separate SSO and the user's bypass cookie
 * is scoped to the branch alias, not the hash URL, so an SSR fetch to
 * VERCEL_URL would 401 even with cookies forwarded).
 */
export function createApiClient(headers?: Record<string, string>, baseUrl?: string) {
  const link = new RPCLink({
    url: `${baseUrl ?? resolveApiUrl()}/rpc`,
    // Always include x-csrf-token for SimpleCsrfProtectionHandlerPlugin.
    // Merge with caller-provided headers (e.g. cookie for SSR).
    headers: () => ({ 'x-csrf-token': 'orpc', ...headers }),
    fetch: (req, init) => fetch(req, { ...init, credentials: 'include' }),
  });
  return createORPCClient<Client>(link);
}
